'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { InterviewQuestion, updateInterviewStatus } from '@/lib/api-client'
import { motion, AnimatePresence } from 'framer-motion'
import * as UpChunk from '@mux/upchunk'

interface DeviceConfig {
  videoId: string
  audioId: string
}

interface SequentialInterviewSessionProps {
  interviewId: string
  token?: string
  otpProofToken?: string
  questions: InterviewQuestion[]
  deviceConfig: DeviceConfig
  onEnd: () => void
  interviewInfo?: {
    candidateName?: string
    jobTitle?: string | null
  }
}

interface QuestionRecording {
  questionId: string
  questionOrder: number
  questionText: string
  durationSeconds: number
  transcript: string
  blob?: Blob
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'failed'
  recordingUrl?: string
  recordingKey?: string
}

function upsertRecording(
  recordings: QuestionRecording[],
  recording: QuestionRecording
): QuestionRecording[] {
  return [
    ...recordings.filter((item) => item.questionOrder !== recording.questionOrder),
    recording,
  ].sort((a, b) => a.questionOrder - b.questionOrder)
}

const UPLOAD_TIMEOUT_MS = 300_000

function waitForMediaRecorderStop(recorder: MediaRecorder): Promise<void> {
  if (recorder.state === 'inactive') {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    recorder.addEventListener('stop', () => resolve(), { once: true })
    recorder.stop()
  })
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    }),
  ])
}

const SequentialInterviewSession: React.FC<SequentialInterviewSessionProps> = ({
  interviewId,
  questions,
  deviceConfig,
  onEnd,
  interviewInfo,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isRecording, setIsRecording] = useState(false)
  const [timeLeft, setTimeLeft] = useState(
    questions[0]?.duration ? questions[0].duration * 60 : 120 // Default 2 min per question
  )
  const [isVideoEnabled, setIsVideoEnabled] = useState(true)
  const [isMicEnabled, setIsMicEnabled] = useState(true)
  const [toast, setToast] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const [autoStartCountdown, setAutoStartCountdown] = useState<number | null>(null)
  const [cameraReady, setCameraReady] = useState(false)

  // Recordings storage
  const [recordings, setRecordings] = useState<QuestionRecording[]>([])
  const pendingRecordingRef = useRef<QuestionRecording | null>(null)
  const transcriptRef = useRef<string>('')
  // Track pending uploads to prevent race conditions
  const pendingUploadsRef = useRef<number>(0)
  const backgroundUploadsRef = useRef<Map<number, Promise<QuestionRecording>>>(new Map())

  // Media refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const startTimeRef = useRef<Date | null>(null)
  const interviewStartTimeRef = useRef<Date | null>(null)
  const countdownTimerRef = useRef<number | null>(null)

  // Question data
  const currentQuestion = questions[currentIndex]
  const isLastQuestion = currentIndex === questions.length - 1

  // API base URL
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002'

  const showToast = (message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3000)
  }

  const extractApiErrorMessage = async (response: Response, fallback: string) => {
    try {
      const payload = await response.json()
      return payload?.message || payload?.error || fallback
    } catch {
      return fallback
    }
  }

  // Upload a single question recording to Mux and persist metadata
  const uploadQuestionRecording = async (
    recording: QuestionRecording
  ): Promise<QuestionRecording> => {
    if (!recording.blob) {
      return { ...recording, uploadStatus: 'failed' }
    }

    try {
      // 1. Create question response placeholder to get the responseId
      const createResponse = await fetch(
        `${apiBaseUrl}/api/v1/jobs/interviews/${interviewId}/question-responses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            questionId: recording.questionId,
            questionOrder: recording.questionOrder,
            questionText: recording.questionText,
            recordingDurationSeconds: recording.durationSeconds,
            recordingSizeBytes: recording.blob.size,
            recordingMimeType: recording.blob.type || 'video/webm',
            transcript: recording.transcript,
            startedAt: startTimeRef.current?.toISOString(),
            completedAt: new Date().toISOString(),
          }),
        }
      )

      if (!createResponse.ok) {
        const message = await extractApiErrorMessage(
          createResponse,
          `Failed to create question response: ${createResponse.statusText}`
        )
        throw new Error(message)
      }

      const createResult = await createResponse.json()
      const responseId = createResult.data?.id

      if (!responseId) {
        throw new Error('No response ID returned from question response creation')
      }

      // 2. Get Mux direct upload URL
      const muxUploadResponse = await fetch(
        `${apiBaseUrl}/api/v1/jobs/interviews/${interviewId}/question-responses/${responseId}/mux-upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ corsOrigin: window.location.origin }),
        }
      )

      if (!muxUploadResponse.ok) {
        const message = await extractApiErrorMessage(
          muxUploadResponse,
          `Failed to create Mux upload: ${muxUploadResponse.statusText}`
        )
        throw new Error(message)
      }

      const muxResult = await muxUploadResponse.json()
      const { uploadUrl, uploadState } = muxResult.data

      if (uploadState === 'asset_ready' || !uploadUrl) {
        return { ...recording, uploadStatus: 'completed' }
      }

      // 3. Upload to Mux via upchunk
      const file = new File([recording.blob!], `question-${recording.questionOrder}.webm`, {
        type: recording.blob!.type || 'video/webm',
      })
      await new Promise<void>((resolve, reject) => {
        const upload = UpChunk.createUpload({
          endpoint: uploadUrl,
          file,
          chunkSize: 5120,
        })

        upload.on('error', (err: any) => {
          reject(new Error(err.detail || 'Upload failed'))
        })

        upload.on('success', () => {
          resolve()
        })
      })

      return { ...recording, uploadStatus: 'completed' }
    } catch (err) {
      return { ...recording, uploadStatus: 'failed' }
    }
  }

  const uploadQuestionRecordingWithRetry = async (
    recording: QuestionRecording
  ): Promise<QuestionRecording> => {
    const maxAttempts = 3
    let attempt = 0
    let lastResult = recording
    while (attempt < maxAttempts) {
      attempt += 1
      try {
        lastResult = await withTimeout(
          uploadQuestionRecording(recording),
          UPLOAD_TIMEOUT_MS,
          'Upload'
        )
      } catch {
        lastResult = { ...recording, uploadStatus: 'failed' }
      }
      if (lastResult.uploadStatus === 'completed') {
        return lastResult
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
    }
    return lastResult
  }

  // Initialize camera
  const initializeCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }

      const constraints: MediaStreamConstraints = {
        video: deviceConfig.videoId
          ? {
              deviceId: { exact: deviceConfig.videoId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: deviceConfig.audioId
          ? {
              deviceId: { exact: deviceConfig.audioId },
              echoCancellation: true,
              noiseSuppression: true,
            }
          : { echoCancellation: true, noiseSuppression: true },
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream
      setCameraReady(true)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.muted = true
      }

      // Track interview start
      if (!interviewStartTimeRef.current) {
        interviewStartTimeRef.current = new Date()
      }
    } catch (err) {
      setCameraReady(false)
      showToast('Failed to access camera/microphone')
    }
  }, [deviceConfig])

  useEffect(() => {
    initializeCamera()

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      setCameraReady(false)
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [initializeCamera])

  // Re-bind video srcObject on question change
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
    }

    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
    setAutoStartCountdown(null)

    if (!cameraReady || isRecording || showConfirmModal || isEnding) {
      return
    }

    setAutoStartCountdown(10)
    countdownTimerRef.current = window.setInterval(() => {
      setAutoStartCountdown((prev) => {
        if (prev === null) {
          return null
        }

        if (prev <= 1) {
          if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
          startRecording().catch((error) => {
            showToast('Failed to start recording')
          })
          return null
        }

        return prev - 1
      })
    }, 1000)

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
    }
  }, [cameraReady, currentIndex, isRecording, showConfirmModal, isEnding])

  // Timer effect
  useEffect(() => {
    if (isRecording && timeLeft > 0) {
      timerRef.current = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleStopRecording()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      if (timerRef.current) clearInterval(timerRef.current)
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, timeLeft])

  const startRecording = async () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
    setAutoStartCountdown(null)

    if (!streamRef.current) {
      showToast('Camera not ready')
      throw new Error('Camera not ready')
    }

    chunksRef.current = []
    transcriptRef.current = ''
    startTimeRef.current = new Date()

    try {
      // Create MediaRecorder for this question
      const supportedMimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ]
      const mimeType =
        supportedMimeTypes.find((t) => MediaRecorder.isTypeSupported(t)) || 'video/webm'

      const recorder = new MediaRecorder(streamRef.current, {
        mimeType,
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 2500000,
      })

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorderRef.current = recorder
      recorder.start(1000) // Collect data every second
      setIsRecording(true)
    } catch (err) {
      showToast('Failed to start recording')
      throw err
    }
  }

  const handleStopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      await waitForMediaRecorderStop(mediaRecorderRef.current)
    }
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current)
    setAutoStartCountdown(null)

    const duration = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000)
      : 0

    const blob = new Blob(chunksRef.current, { type: 'video/webm' })

    const newRecording: QuestionRecording = {
      questionId: currentQuestion.id,
      questionOrder: currentIndex,
      questionText: currentQuestion.content || currentQuestion.title,
      durationSeconds: duration,
      transcript: transcriptRef.current.trim(),
      blob: blob.size > 0 ? blob : undefined,
      uploadStatus: 'pending',
    }

    setIsRecording(false)

    if (isLastQuestion) {
      pendingRecordingRef.current = newRecording
      setShowConfirmModal(true)
      return
    }

    const nextIndex = currentIndex + 1
    setCurrentIndex(nextIndex)

    const nextQuestionDuration = questions[nextIndex]?.duration
      ? questions[nextIndex].duration * 60
      : 120
    setTimeLeft(nextQuestionDuration)

    showToast(`Moving to Question ${nextIndex + 1}`)

    if (!newRecording.blob) {
      showToast(`Question ${newRecording.questionOrder + 1} had no recording data`)
      setRecordings((prev) => upsertRecording(prev, { ...newRecording, uploadStatus: 'failed' }))
      return
    }

    // ponytail: mid-question uploads run in background; never block the next recording
    const uploadPromise = uploadQuestionRecordingWithRetry(newRecording)
    backgroundUploadsRef.current.set(newRecording.questionOrder, uploadPromise)

    void uploadPromise
      .then((uploadedRecording) => {
        setRecordings((prev) => upsertRecording(prev, uploadedRecording))
        if (uploadedRecording.uploadStatus !== 'completed') {
          showToast(`Upload for question ${newRecording.questionOrder + 1} failed. Will retry.`)
        }
      })
      .catch(() => {
        setRecordings((prev) => upsertRecording(prev, { ...newRecording, uploadStatus: 'failed' }))
        showToast(`Upload for question ${newRecording.questionOrder + 1} failed. Will retry.`)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentQuestion, isLastQuestion, questions])

  const handleConfirmSubmit = async () => {
    if (!pendingRecordingRef.current) return

    setIsEnding(true)
    setShowConfirmModal(false)

    // Increment pending uploads counter to prevent background uploads from hiding overlay
    pendingUploadsRef.current += 1
    setIsUploading(true)
    setUploadProgress('Uploading final response...')

    try {
      const backgroundResults = await Promise.all(backgroundUploadsRef.current.values())
      const savedRecordings = new Map(
        recordings.map((recording) => [recording.questionOrder, recording])
      )
      for (const recording of backgroundResults) {
        savedRecordings.set(recording.questionOrder, recording)
      }

      const failedRecordings = [...savedRecordings.values()].filter(
        (recording) => recording.uploadStatus !== 'completed' && recording.blob
      )

      if (failedRecordings.length > 0) {
        setUploadProgress(
          `Uploading ${failedRecordings.length} saved response${failedRecordings.length === 1 ? '' : 's'}...`
        )

        for (const recording of failedRecordings) {
          const uploadedRecording = await uploadQuestionRecordingWithRetry(recording)
          setRecordings((prev) => upsertRecording(prev, uploadedRecording))

          if (uploadedRecording.uploadStatus !== 'completed') {
            throw new Error(`Response ${recording.questionOrder + 1} upload did not complete`)
          }
        }

        setUploadProgress('Uploading final response...')
      }

      // Do not finalize or navigate away until the final response upload completes.
      const uploadedRecording = await uploadQuestionRecordingWithRetry(pendingRecordingRef.current)

      if (uploadedRecording.uploadStatus !== 'completed') {
        setRecordings((prev) =>
          upsertRecording(prev, { ...uploadedRecording, uploadStatus: 'failed' })
        )
        throw new Error('Final response upload did not complete')
      }

      setRecordings((prev) => upsertRecording(prev, uploadedRecording))

      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1)
      setUploadProgress('Finalizing interview...')

      // Calculate total duration (don't wait for upload)
      const allRecordings = [...recordings, pendingRecordingRef.current]
      const totalDuration = interviewStartTimeRef.current
        ? Math.floor((Date.now() - interviewStartTimeRef.current.getTime()) / 1000)
        : allRecordings.reduce((sum, r) => sum + r.durationSeconds, 0)

      // Only mark the interview complete after the final response upload succeeded.
      await updateInterviewStatus(interviewId, totalDuration)

      showToast('Interview submitted successfully!')

      // Clean up
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }

      // Hide overlay after a brief delay
      setTimeout(() => {
        if (pendingUploadsRef.current === 0) {
          setIsUploading(false)
          setUploadProgress(null)
        }
      }, 500)

      // Callback to parent
      setTimeout(onEnd, 400)
    } catch (err) {
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1)
      setIsEnding(false)
      setIsUploading(false)
      setUploadProgress(null)
      setShowConfirmModal(true)
      showToast('Failed to submit - please try again')
    }
  }

  const toggleVideo = () => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = !isVideoEnabled
      })
      setIsVideoEnabled(!isVideoEnabled)
    }
  }

  const toggleMic = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !isMicEnabled
      })
      setIsMicEnabled(!isMicEnabled)
    }
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const actionLabel = isLastQuestion ? 'Save & Submit' : 'Save & Next'

  return (
    <div className="min-h-screen bg-[#f5f7f2] text-[#18201a]">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[100] flex -translate-x-1/2 items-center space-x-4 rounded-full bg-[#1a211c] px-6 py-3 text-white shadow-2xl animate-fade-in">
          <svg
            className="w-5 h-5 text-brand-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-bold uppercase tracking-widest">{toast}</span>
        </div>
      )}

      <AnimatePresence>
        {isUploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(17, 24, 19, 0.74)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <div className="w-full max-w-sm rounded-[28px] border border-white/30 bg-[#f8fbf3] px-8 py-10 text-center shadow-[0_32px_80px_rgba(0,0,0,0.24)]">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 20,
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    margin: '0 auto 24px',
                    border: '4px solid rgba(24, 32, 26, 0.08)',
                    borderTop: '4px solid #c0fc71',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <h3 className="mb-2 text-xl font-semibold text-[#18201a]">
                  {uploadProgress ||
                    (isEnding || isLastQuestion
                      ? 'Completing interview...'
                      : 'Moving to next screen...')}
                </h3>
                <p className="text-sm text-[#4f5a51]">
                  {isEnding || isLastQuestion
                    ? 'Please wait while we finalize your interview...'
                    : 'Please wait while we prepare the next question...'}
                </p>
              </motion.div>
            </div>
            <style>{`
              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#101612]/35 p-6 backdrop-blur-md">
          <div className="w-full max-w-md rounded-[32px] border border-[#dbe4d5] bg-[#fbfdf8] p-10 shadow-[0_32px_90px_rgba(0,0,0,0.18)] animate-fade-in">
            <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full border border-[#dbe4d5] bg-[#eef6dd]">
              <svg
                className="h-6 w-6 text-[#557a27]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </div>
            <h3 className="mb-3 text-center text-2xl font-semibold text-[#18201a]">
              Complete interview?
            </h3>
            <p className="mb-8 text-center text-sm leading-6 text-[#566056]">
              You&apos;ve completed all {questions.length} questions. Would you like to submit your
              responses now?
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleConfirmSubmit}
                disabled={isEnding}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#d2f34c] px-6 py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#1a2417] transition-colors hover:bg-[#c6ea36] disabled:cursor-not-allowed disabled:bg-[#dbe5cf]"
              >
                {isEnding ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#1a2417]/20 border-t-[#1a2417]" />
                    {uploadProgress || 'Submitting...'}
                  </>
                ) : (
                  'Submit Interview'
                )}
              </button>
              {!isEnding && (
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="w-full rounded-full border border-[#dce5d6] px-6 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#445044] transition-colors hover:bg-[#f4f8ef]"
                >
                  Go Back
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-6 sm:px-6 sm:py-8">
        <div className="rounded-[34px] border border-[#d9e1d5] bg-[#fcfdf9] p-5 shadow-[0_30px_80px_rgba(34,45,32,0.08)] sm:p-8">
          <div className="rounded-[28px] border border-[#e4ebe0] bg-[#f7faf2] px-5 py-5 sm:px-7">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-2xl">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#72806f]">
                    Interview question
                  </p>
                  <h1 className="mt-3 text-3xl font-semibold leading-tight text-[#18201a] sm:text-4xl">
                    {currentQuestion?.content || currentQuestion?.title}
                  </h1>
                  <p className="mt-4 max-w-xl text-sm leading-6 text-[#576257]">
                    Answer one question at a time. Record your response, save it, then continue to
                    the next prompt.
                  </p>
                </div>
                <div className="min-w-[150px] rounded-[22px] border border-[#dce5d6] bg-white px-4 py-3 text-right shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#7b877a]">
                    Question
                  </p>
                  <p className="mt-2 text-xl font-semibold text-[#18201a]">
                    {currentIndex + 1} <span className="text-[#7b877a]">of {questions.length}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {questions.map((question, idx) => (
                  <div
                    key={question.id || idx}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx < currentIndex
                        ? 'w-10 bg-[#a9c94d]'
                        : idx === currentIndex
                          ? 'w-14 bg-[#d2f34c]'
                          : 'w-8 bg-[#dce4d4]'
                    }`}
                  />
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="rounded-full border border-[#dce5d6] bg-white px-4 py-2 font-medium text-[#314033]">
                  {formatTime(timeLeft)} remaining
                </div>
                <div
                  className={`rounded-full border px-4 py-2 font-medium ${
                    isRecording
                      ? 'border-[#e6b9b2] bg-[#fff4f1] text-[#9a3a2d]'
                      : 'border-[#dce5d6] bg-white text-[#59665b]'
                  }`}
                >
                  {isRecording ? 'Recording in progress' : 'Ready to record'}
                </div>
                {interviewInfo?.jobTitle && (
                  <div className="rounded-full border border-[#dce5d6] bg-white px-4 py-2 font-medium text-[#59665b]">
                    {interviewInfo.jobTitle}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-[28px] border border-[#11171d] bg-[#10141a] p-3 shadow-[0_24px_70px_rgba(12,15,20,0.22)] sm:mt-8 sm:p-4">
            <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[24px] bg-[#050608] sm:aspect-video">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`h-full w-full object-cover transform scale-x-[-1] transition-opacity duration-500 ${!isVideoEnabled ? 'opacity-0' : 'opacity-100'}`}
              />

              {!isVideoEnabled && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#141922]">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-white/5">
                    <svg
                      className="h-8 w-8 text-white/30"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3l18 18"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-white/55">Camera Off</p>
                </div>
              )}

              {autoStartCountdown !== null && !isRecording && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/45 backdrop-blur-sm">
                  <motion.div
                    key={autoStartCountdown}
                    initial={{ scale: 1.08, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="text-6xl font-bold text-white tabular-nums"
                  >
                    {autoStartCountdown}
                  </motion.div>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-white/75">
                    Recording will auto-start
                  </p>
                </div>
              )}

              <div className="absolute left-4 top-4 flex items-center gap-3">
                <div className="rounded-full border border-white/10 bg-black/72 px-4 py-2 backdrop-blur-md">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-white/25'}`}
                    />
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white">
                      {isRecording ? 'Recording' : 'Ready'}
                    </span>
                  </div>
                </div>
              </div>

              <div
                className={`absolute right-4 top-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-black/78 px-5 py-3 backdrop-blur-xl shadow-2xl ${timeLeft < 30 ? 'animate-pulse' : ''}`}
              >
                <svg
                  className={`h-5 w-5 ${timeLeft < 30 ? 'text-red-500' : 'text-white'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  className={`font-mono text-2xl font-bold tracking-tight ${timeLeft < 30 ? 'text-red-500' : 'text-white'}`}
                >
                  {formatTime(timeLeft)}
                </span>
              </div>

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/84 via-black/34 to-transparent px-4 pb-4 pt-20 sm:px-6 sm:pb-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex items-center gap-3 rounded-full border border-white/10 bg-black/56 px-5 py-3 backdrop-blur-xl">
                    <button
                      onClick={toggleMic}
                      className={`rounded-full p-3 transition-all ${isMicEnabled ? 'bg-white/10 text-white hover:bg-white/18' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {isMicEnabled ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                          />
                        </svg>
                      )}
                    </button>
                    <div className="h-6 w-px bg-white/10" />
                    <button
                      onClick={toggleVideo}
                      className={`rounded-full p-3 transition-all ${isVideoEnabled ? 'bg-white/10 text-white hover:bg-white/18' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {isVideoEnabled ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 3l18 18"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {!isRecording ? (
                      <button
                        onClick={async () => {
                          try {
                            await startRecording()
                          } catch (error) {
                            void error
                          }
                        }}
                        disabled={isEnding || !cameraReady}
                        className="rounded-full bg-[#d2f34c] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-[#1d281e] transition-all hover:bg-[#c6ea36] active:scale-95 disabled:bg-white/10 disabled:text-white/35"
                      >
                        <span className="mr-3 inline-block h-2.5 w-2.5 rounded-full bg-[#1d281e]" />
                        {cameraReady
                          ? autoStartCountdown !== null
                            ? `Start Recording (${autoStartCountdown}s)`
                            : 'Start Recording'
                          : 'Preparing Camera'}
                      </button>
                    ) : (
                      <button
                        onClick={handleStopRecording}
                        className="rounded-full bg-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-[#0f1419] transition-all active:scale-95 shadow-xl"
                      >
                        <span className="mr-3 inline-block h-2.5 w-2.5 rounded-sm bg-red-500" />
                        {actionLabel}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {interviewInfo?.candidateName && (
            <div className="mt-5 text-center text-xs text-[#6d786d]">
              Candidate:{' '}
              <span className="font-medium text-[#18201a]">{interviewInfo.candidateName}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SequentialInterviewSession
